import React, { useMemo } from 'react';
import { Icon } from '@chargify/icons';

import { BodyText } from '../../atoms';
import { Container, Page, Navigation } from './Pagination.styles';

import { createPagination } from './utils';
import { KEYBOARD_KEYS } from '../../../constants';

type PaginationProps = {
  /**
   * Current page
   */
  page?: number;
  /**
   * Number of total pages
   */
  totalPages: number;
  /**
   * Change event handler
   */
  onChange: (page: number) => void;
};

/**
- The Pagination component provides user interface to navigate through different pages.
**/
const Pagination = ({ page = 1, totalPages, onChange }: PaginationProps) => {
  const paginationItems = useMemo(
    () => createPagination({ currentPage: page, totalPages }),
    [page, totalPages]
  );

  const backDisabled = page === 1;
  const nextDisabled = page === totalPages;

  const onNavigateBack = () => onChange(page - 1);
  const onNavigateNext = () => onChange(page + 1);

  return (
    <Container gridGap="4">
      <Navigation
        isDisabled={backDisabled}
        tabIndex={0}
        data-testid="pagination-back"
        onKeyDown={(e) => {
          if (!backDisabled && e.key === KEYBOARD_KEYS.ENTER) {
            onNavigateBack();
          }
        }}
        onClick={() => {
          if (!backDisabled) onNavigateBack();
        }}
      >
        <Icon type="chevronLeft" size={12} fill="inherit" />
      </Navigation>
      {paginationItems.map((item, idx) => (
        <React.Fragment key={`page-${idx}-${item}`}>
          {typeof item === 'number' ? (
            <Page
              role="button"
              isActive={item === page}
              tabIndex={0}
              display="flex"
              minWidth="4"
              height="4"
              onClick={() => {
                if (item !== page) onChange(item);
              }}
              onKeyDown={(e) => {
                if (item !== page && e.key === KEYBOARD_KEYS.ENTER) {
                  onChange(item);
                }
              }}
            >
              <BodyText variant="body2" color="inherit" fontWeight={2}>
                {item}
              </BodyText>
            </Page>
          ) : (
            <BodyText variant="body2" color="primary.dark" fontWeight={2}>
              {item}
            </BodyText>
          )}
        </React.Fragment>
      ))}
      <Navigation
        isDisabled={nextDisabled}
        tabIndex={0}
        data-testid="pagination-next"
        onKeyDown={(e) => {
          if (!nextDisabled && e.key === KEYBOARD_KEYS.ENTER) {
            onNavigateNext();
          }
        }}
        onClick={() => {
          if (!nextDisabled) onNavigateNext();
        }}
      >
        <Icon type="chevronRight" size={12} fill="inherit" />
      </Navigation>
    </Container>
  );
};

export default Pagination;
